import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";

function FarmerUpdate() {
    const [farmer, setFarmer] = useState({
        first_name: "",
        middle_name: "",
        last_name: "",
        phone_number: "",
        id_number: "",
        account_number: "",
        account_type: "",
        route_id: "",
        created_by: "",
        updated_by: ""
    });
    const [collectionRoutes, setCollectionRoutes] = useState([]);
    const [accountTypes, setAccountTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");

    useEffect(() => {
        const fetchCollectionRoutes = async () => {
            try {
                const response = await fetch(`${API_URL}/collection-routes`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setCollectionRoutes(data);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch routes");
                }


            } catch (error) {
                setNotification({ type: "danger", message: "Failed to fetch collection Routes." });
            }
        };

        const fetchAccountTypes = async () => {
            try {
                const response = await fetch(`${API_URL}/account-types`);
                const data = await response.json();
                setAccountTypes(data.accountTypes);
            } catch (error) {
                setNotification({ type: "danger", message: "Failed to fetch account types." });
            }
        };

        const fetchFarmer = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/farmers/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setFarmer(data);
                        setIsEditing(true);
                    } else if (response.status === 401) {
                        navigate("/logout");
                    } else {
                        throw new Error("Failed to fetch farmer details");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the farmer." });
                }
            }
        };

        const fetchData = async () => {
            await fetchCollectionRoutes();
            await fetchAccountTypes();
            await fetchFarmer();
            setLoading(false);
        };

        fetchData();
    }, [id, token]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFarmer((prevFarmer) => ({
            ...prevFarmer,
            [name]: value || "",
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrors({});

        if (!farmer.last_name && !farmer.middle_name) {
            setErrors(prevErrors => ({
                ...prevErrors,
                last_name: ["Enter either last name or middle name"], // Store errors as an array
                middle_name: ["Enter either last name or middle name"]
            }));
            return; // Stop form submission if there's an error
        }

        // Validate account_number based on account_type
        const accountType = farmer.account_type?.toLowerCase();
        if (accountType !== "cash" && accountType !== "mpesa" && !farmer.account_number) {
            setErrors(prevErrors => ({
                ...prevErrors,
                account_number: ["Account number is required for this account type."]
            }));
            return;
        }

        const method = "POST";
        const url = isEditing ? `${API_URL}/farmers/${id}/update` : `${API_URL}/farmers`;

        const formData = new FormData();

        for (const key in farmer) {
            if (farmer[key] === null || farmer[key] === "") {
                formData.delete(key);
            } else {
                formData.append(key, farmer[key]);
            }
        }

        try {
            setLoading(true);
            const response = await fetch(url, {
                method,
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                navigate(`/dashboard/farmers/${data.id}/details`);
            } else {
                const errorData = await response.json();
                setErrors(errorData || {});
                setNotification({ message: errorData.message || "An error occurred.", type: "danger" });
            }
        } catch (error) {
            setNotification({ message: "An error occurred while saving the farmer.", type: "danger" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">{isEditing ? "Update Farmer" : "Add Farmer"}</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/farmers" className="button primary-bg">
                            <i className="fa fa-arrow-left"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>
            {notification.message && (
                <Notification type={notification.type} message={notification.message} onClose={() => setNotification({})} />
            )}
            <form onSubmit={handleSubmit}>
                <div className="columns">
                    <div className="column is-half">
                        <div className="field">
                            <label className="label">First Name</label>
                            <div className="control">
                                <input className={`input ${errors.first_name ? "is-danger" : ""}`} type="text" name="first_name" value={farmer.first_name} onChange={handleChange} required />
                            </div>
                            {errors.first_name && errors.first_name.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        <div className="field">
                            <label className="label">Middle Name</label>
                            <div className="control">
                                <input className={`input ${errors.middle_name ? "is-danger" : ""}`} type="text" name="middle_name" value={farmer.middle_name} onChange={handleChange} />
                            </div>
                            {errors.middle_name && errors.middle_name.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        <div className="field">
                            <label className="label">Last Name</label>
                            <div className="control">
                                <input className={`input ${errors.last_name ? "is-danger" : ""}`} type="text" name="last_name" value={farmer.last_name} onChange={handleChange} />
                            </div>
                            {errors.last_name && errors.last_name.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {/* <div className="field">
                            <label className="label">Farmer Number</label>
                            <div className="control">
                                <input className={`input ${errors.farmer_number ? "is-danger" : ""}`} type="number" name="farmer_number" min="0" max="20000" value={farmer.farmer_number} onChange={handleChange} required />
                            </div>
                            {errors.farmer_number && errors.farmer_number.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div> */}

                        <div className="field">
                            <label className="label">ID Number</label>
                            <div className="control">
                                <input className={`input ${errors.id_number ? "is-danger" : ""}`} type="number" name="id_number" min="0" max="1000000000" value={farmer.id_number} onChange={handleChange} required />
                            </div>
                            {errors.id_number && errors.id_number.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        <div className="field">
                            <label className="label">Phone Number</label>
                            <div className="control">
                                <input className={`input ${errors.phone_number ? "is-danger" : ""}`} type="text" name="phone_number" value={farmer.phone_number} onChange={handleChange} required />
                            </div>
                            {errors.phone_number && errors.phone_number.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        <div className="field">
                            <label className="label">Account Number</label>
                            <div className="control">
                                <input className={`input ${errors.account_number ? "is-danger" : ""}`} type="text" name="account_number" value={farmer.account_number} onChange={handleChange} />
                            </div>
                            {errors.account_number && errors.account_number.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        <div className="field">
                            <label className="label">Account Type</label>
                            <div className={`select is-fullwidth ${errors.account_type ? "is-danger" : ""}`}>
                                <select name="account_type" value={farmer.account_type} onChange={handleChange} required>
                                    <option value="">Select Account Type</option>
                                    {accountTypes?.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            {errors.account_type && errors.account_type.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        <div className="field">
                            <label className="label">Collection Route</label>
                            <div className={`select is-fullwidth ${errors.account_number ? "is-danger" : ""}`}>
                                <select name="route_id" value={farmer.route_id} onChange={handleChange}>
                                    <option value="">Select Route</option>
                                    {collectionRoutes?.map((route) => (
                                        <option key={route.id} value={route.id}>{route.name}</option>
                                    ))}
                                </select>
                            </div>
                            {errors.route_id && errors.route_id.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>
                    </div>
                </div>
                <button type="submit" className="button is-primary">Save</button>
            </form>
        </main>
    );
}

export default FarmerUpdate;
